import React, { useState, useEffect } from "react";
import { Phone, List, X, GlobeSimple } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";

export default function Header() {
  const { t, lang, toggleLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: t.nav.home, href: "#inicio" },
    { label: t.nav.services, href: "#servicios" },
    { label: t.nav.materials, href: "#materiales" },
    { label: t.nav.gallery, href: "#galeria" },
    { label: t.nav.testimonials, href: "#testimonios" },
    { label: t.nav.about, href: "#nosotros" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#inicio" className="flex items-center gap-3" data-testid="header-logo">
          <span className="font-['Cormorant_Garamond'] text-2xl font-semibold text-white tracking-tight">
            DOMINICAN
          </span>
          <span className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FFD700] tracking-tight">
            UPHOLSTERED
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-light text-zinc-300 hover:text-[#FFD700] transition-colors duration-300 tracking-wide uppercase"
              data-testid={`nav-link-${link.href.slice(1)}`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-white/20 text-zinc-300 hover:text-[#FFD700] hover:border-[#FFD700] transition-colors text-xs uppercase tracking-wider"
            data-testid="lang-toggle"
          >
            <GlobeSimple size={14} />
            {lang === "en" ? "ES" : "EN"}
          </button>
          <a
            href="tel:9732883496"
            className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#FFD700] transition-colors"
            data-testid="header-phone"
          >
            <Phone size={18} weight="bold" className="text-[#FFD700]" />
            973-288-3496
          </a>
          <a
            href="#cotizacion"
            className="px-6 py-2.5 bg-[#FFD700] text-black text-sm font-medium tracking-wide uppercase hover:bg-[#E5C100] transition-colors duration-300"
            data-testid="header-quote-btn"
          >
            {t.header.quote}
          </a>
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={28} /> : <List size={28} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-base text-zinc-300 hover:text-[#FFD700] transition-colors uppercase tracking-wide"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={toggleLang}
                className="flex items-center gap-2 text-zinc-300 mt-2"
              >
                <GlobeSimple size={18} className="text-[#FFD700]" />
                {lang === "en" ? "Espanol" : "English"}
              </button>
              <a href="tel:9732883496" className="flex items-center gap-2 text-zinc-300 mt-2">
                <Phone size={18} className="text-[#FFD700]" />
                973-288-3496
              </a>
              <a
                href="#cotizacion"
                onClick={() => setMobileOpen(false)}
                className="mt-2 px-6 py-3 bg-[#FFD700] text-black text-center font-medium uppercase tracking-wide"
              >
                {t.header.requestQuote}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
